import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TablerIconsModule} from 'angular-tabler-icons';
import {concatMap, from, Subscription, switchMap, timer} from 'rxjs';
import {MaterialModule} from '@app/material.module';
import {environment} from '@environment/environment';
import {ExplorerEntity, RagStatus} from '@interfaces/explorer-entity';
import {User} from '@interfaces/user';
import {FilemanagerApiService} from '@services/api/filemanager/filemanager-api.service';
import {RagApiService, RagChatReference} from '@services/api/rag/rag-api.service';
import {LogoutService} from '@services/api/logout/logout.service';
import {CoreService} from '@services/core.service';
import {UserStorageService} from '@services/local-storage/user-storage/user-storage.service';
import {ModalsConfirmComponent} from '@app/components/modals/modals-confirm/modals-confirm.component';

interface MessageSource {
  name: string;
  pages: number[];
}

interface DocMessage {
  role: 'user' | 'bot';
  text: string;
  /** Risposta del bot già convertita in HTML sicuro (grassetti, elenchi). */
  html?: string;
  timestamp: Date;
  sources?: MessageSource[];
  error?: boolean;
  copied?: boolean;
}

/** Conversazione legata a un ambito (tutti i documenti o uno solo): cambiando ambito non si perde. */
interface DocConversation {
  sessionId: string;
  messages: DocMessage[];
}

/** Ambito della chat: 'all' = tutti i documenti pronti, altrimenti l'uuid di un documento. */
type ChatScope = 'all' | string;

const ALL: ChatScope = 'all';
const LANG_KEY = 'app_language';
/** Dimensione massima del PDF accettata lato client. */
const MAX_PDF_BYTES = 50 * 1024 * 1024;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function inlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
}

/**
 * Converte il markdown essenziale delle risposte LLM (grassetto, codice, elenchi
 * puntati/numerati, titoletti) in HTML. Il testo viene sempre escapato prima,
 * quindi nessun HTML proveniente dal modello arriva al DOM.
 */
function renderAnswer(answer: string): string {
  const out: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  const closeList = () => {
    if (list) {
      out.push(`</${list}>`);
      list = null;
    }
  };

  for (const raw of escapeHtml(answer ?? '').split('\n')) {
    const line = raw.trim();
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    const heading = line.match(/^#{1,4}\s+(.*)$/);
    if (bullet || numbered) {
      const type = bullet ? 'ul' : 'ol';
      if (list !== type) {
        closeList();
        out.push(`<${type}>`);
        list = type;
      }
      out.push(`<li>${inlineMarkdown((bullet ?? numbered)![1])}</li>`);
    } else if (!line) {
      closeList();
    } else {
      closeList();
      out.push(heading ? `<p><strong>${inlineMarkdown(heading[1])}</strong></p>` : `<p>${inlineMarkdown(line)}</p>`);
    }
  }
  closeList();
  return out.join('');
}

/**
 * Applicazione DM Manufacturing: il cliente carica i suoi PDF, il backend li
 * indicizza nel RAG (worker Celery) e l'assistente risponde alle domande
 * basandosi solo su quei documenti, citando le pagine.
 */
@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [CommonModule, DatePipe, FormsModule, MaterialModule, TablerIconsModule, TranslateModule],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss',
})
export class DocumentsComponent implements OnInit, OnDestroy {
  /** Intervallo tra due letture di stato durante l'indicizzazione. */
  private readonly POLL_INTERVAL_MS = 4000;

  readonly RagStatus = RagStatus;
  readonly ALL = ALL;
  readonly languages = environment.language_list;
  readonly suggestions = ['documents.suggestion_summary', 'documents.suggestion_key_points', 'documents.suggestion_safety'];

  documents: ExplorerEntity[] = [];
  scope: ChatScope = ALL;
  listLoaded = false;
  uploadingCount = 0;
  dragOver = false;

  inputText = '';
  sending = false;
  user: User | null = null;
  currentLanguage = environment.language_default;
  private conversations = new Map<ChatScope, DocConversation>();
  /** Blob URL dei PDF aperti in un'altra scheda, revocati all'uscita dalla pagina. */
  private pdfUrls: string[] = [];

  private filemanagerApi = inject(FilemanagerApiService);
  private ragApi = inject(RagApiService);
  private coreService = inject(CoreService);
  private translate = inject(TranslateService);
  private titleService = inject(Title);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private logoutService = inject(LogoutService);
  private userStorage = inject(UserStorageService);
  private pollSub?: Subscription;
  private userSub?: Subscription;
  private langSub?: Subscription;

  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('chatInput') chatInput?: ElementRef<HTMLTextAreaElement>;

  ngOnInit(): void {
    // Questa pagina non usa l'header del vecchio layout: la lingua salvata va ripristinata qui
    const saved = localStorage.getItem(LANG_KEY);
    if (saved && this.languages.some((l) => l.code === saved)) {
      this.setLanguage(saved);
    } else {
      // Senza preferenza salvata si usa la lingua di default dell'app (senza memorizzarla)
      this.currentLanguage = this.translate.currentLang || environment.language_default;
      this.translate.use(this.currentLanguage);
      this.coreService.setLanguage(this.currentLanguage);
    }
    // stream() riemette quando le traduzioni arrivano e a ogni cambio lingua
    this.langSub = this.translate.stream('documents.title').subscribe(
      (title: string) => this.titleService.setTitle(title + environment.second_title),
    );

    this.userSub = this.userStorage.user$.subscribe((user) => (this.user = user));
    this.userStorage.getUserStorage().then((user) => (this.user = this.user ?? user));

    this.loadDocuments();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.userSub?.unsubscribe();
    this.langSub?.unsubscribe();
    this.pdfUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  // ---------------------------------------------------------------------------
  // Stato derivato
  // ---------------------------------------------------------------------------

  get readyDocuments(): ExplorerEntity[] {
    return this.documents.filter((d) => this.isReady(d));
  }

  get selectedDocument(): ExplorerEntity | null {
    return this.scope === ALL ? null : this.documents.find((d) => d.uuid === this.scope) ?? null;
  }

  /** Vero se l'ambito corrente ha almeno un documento interrogabile. */
  get canChat(): boolean {
    const doc = this.selectedDocument;
    return doc ? this.isReady(doc) : this.readyDocuments.length > 0;
  }

  get messages(): DocMessage[] {
    return this.conversation(this.scope).messages;
  }

  get userInitials(): string {
    const name = `${this.user?.name ?? ''} ${this.user?.lastname ?? ''}`.trim();
    const source = name || this.user?.email || '?';
    return source.split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  }

  isReady(doc: ExplorerEntity): boolean {
    return doc.rag_status === RagStatus.ANALYZED && !!doc.rag_document_id;
  }

  // ---------------------------------------------------------------------------
  // Elenco documenti
  // ---------------------------------------------------------------------------

  private loadDocuments(loaderShow = true): void {
    this.filemanagerApi.getList(null, '-created_at', loaderShow).subscribe({
      next: (response) => {
        this.applyList((response?.data ?? []) as ExplorerEntity[]);
        this.listLoaded = true;
      },
      error: () => {
        this.listLoaded = true;
        this.showError('documents.error_load');
      },
    });
  }

  private applyList(items: ExplorerEntity[]): void {
    this.documents = items.filter((it) => it.type === 'file' && this.isPdf(it));
    if (this.scope !== ALL && !this.documents.some((d) => d.uuid === this.scope)) {
      this.scope = ALL;
    }
    this.syncPolling();
  }

  private isPdf(item: ExplorerEntity): boolean {
    const name = String(item['original_filename'] ?? item.name ?? '').toLowerCase();
    return item.content_type === 'application/pdf' || name.endsWith('.pdf');
  }

  selectScope(scope: ChatScope): void {
    this.scope = scope;
    this.inputText = '';
    this.scrollToBottom();
  }

  /** Avvia il polling silenzioso solo finché c'è almeno un documento in indicizzazione. */
  private syncPolling(): void {
    const hasPending = this.documents.some((d) => d.rag_status === RagStatus.PENDING);
    if (!hasPending) {
      this.stopPolling();
      return;
    }
    if (this.pollSub) {
      return;
    }
    this.pollSub = timer(this.POLL_INTERVAL_MS, this.POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.filemanagerApi.getList(null, '-created_at', false)))
      .subscribe({
        next: (response) => this.applyList((response?.data ?? []) as ExplorerEntity[]),
        error: () => this.stopPolling(),
      });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  // ---------------------------------------------------------------------------
  // Caricamento (click o drag & drop), reindicizzazione ed eliminazione
  // ---------------------------------------------------------------------------

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.uploadFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    this.uploadFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  private uploadFiles(files: File[]): void {
    const pdfs = files.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length < files.length) {
      this.showError('documents.error_not_pdf');
    }
    const valid = pdfs.filter((f) => f.size <= MAX_PDF_BYTES);
    if (valid.length < pdfs.length) {
      this.showError('documents.error_too_big');
    }
    if (valid.length === 0) {
      return;
    }

    this.uploadingCount += valid.length;
    // Upload in sequenza: ogni PDF viaggia in base64 nel body, meglio non saturare la banda
    from(valid).pipe(concatMap((file) => this.uploadOne(file))).subscribe();
  }

  private uploadOne(file: File): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        this.uploadingCount = Math.max(0, this.uploadingCount - 1);
        resolve();
      };
      const reader = new FileReader();
      reader.onload = () => {
        // Forziamo il mime PDF: alcuni browser lasciano il data URL generico
        const base64 = String(reader.result).replace(/^data:[^;]*;base64,/, 'data:application/pdf;base64,');
        const body = {
          name: file.name.replace(/\.pdf$/i, ''),
          type: 'file',
          parent: null,
          file_base64: base64,
        } as unknown as ExplorerEntity;  // l'id lo assegna il backend

        this.filemanagerApi.uploadFile(body).subscribe({
          next: (response) => {
            const uploaded = response.data;
            if (uploaded?.uuid) {
              this.startIndexing(uploaded);
            } else {
              this.loadDocuments(false);
            }
            done();
          },
          error: () => {
            this.showError('documents.error_upload');
            done();
          },
        });
      };
      reader.onerror = () => {
        this.showError('documents.error_upload');
        done();
      };
      reader.readAsDataURL(file);
    });
  }

  /** Mette in coda l'indicizzazione RAG del documento (anche per riprovare dopo un errore). */
  startIndexing(doc: ExplorerEntity, event?: Event): void {
    event?.stopPropagation();
    if (!doc.uuid) {
      return;
    }
    this.filemanagerApi.sendToRag(doc.uuid).subscribe({
      next: () => this.loadDocuments(false),
      error: () => this.loadDocuments(false),
    });
  }

  deleteDocument(doc: ExplorerEntity, event: Event): void {
    event.stopPropagation();
    const ref = this.dialog.open(ModalsConfirmComponent, {
      panelClass: 'dm-dialog',
      data: {
        title: 'documents.delete_title',
        message: 'documents.delete_message',
        confirmText: 'documents.delete_confirm',
        cancelText: 'documents.cancel',
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      (this.filemanagerApi.deleteItem(doc.id) as any).subscribe({
        next: () => {
          if (doc.uuid) {
            this.conversations.delete(doc.uuid);
          }
          // La conversazione su "tutti" citava anche questo documento: si riparte puliti
          this.conversations.delete(ALL);
          if (this.scope === doc.uuid) {
            this.scope = ALL;
          }
          this.loadDocuments();
        },
        error: () => this.showError('documents.error_delete'),
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Chat
  // ---------------------------------------------------------------------------

  private conversation(scope: ChatScope): DocConversation {
    let conv = this.conversations.get(scope);
    if (!conv) {
      conv = {sessionId: crypto.randomUUID(), messages: []};
      this.conversations.set(scope, conv);
    }
    return conv;
  }

  newConversation(): void {
    this.conversations.set(this.scope, {sessionId: crypto.randomUUID(), messages: []});
    this.inputText = '';
  }

  useSuggestion(key: string): void {
    this.inputText = this.translate.instant(key);
    this.sendMessage();
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    this.sendMessage();
  }

  sendMessage(): void {
    const text = this.inputText.trim();
    if (!text || this.sending || !this.canChat) {
      return;
    }
    const doc = this.selectedDocument;
    const documentIds = (doc ? [doc] : this.readyDocuments).map((d) => d.rag_document_id as string);

    const conv = this.conversation(this.scope);
    conv.messages.push({role: 'user', text, timestamp: new Date()});
    this.inputText = '';
    this.sending = true;
    this.resetInputHeight();
    this.scrollToBottom();

    const language = this.currentLanguage.toUpperCase();

    this.ragApi.chat({
      session_id: conv.sessionId,
      message: text,
      language,
      company_id: 0,  // ignorato dal backend: usa l'azienda dell'utente autenticato
      knowledge_scope: 'uploaded',
      document_ids: documentIds,
    }).subscribe({
      next: (res) => {
        conv.messages.push({
          role: 'bot',
          text: res.data.answer,
          html: renderAnswer(res.data.answer),
          timestamp: new Date(),
          sources: this.groupSources(res.data.references),
        });
        this.sending = false;
        this.scrollToBottom();
      },
      error: () => {
        conv.messages.push({role: 'bot', text: this.translate.instant('documents.error_chat'), timestamp: new Date(), error: true});
        this.sending = false;
        this.scrollToBottom();
      },
    });
  }

  /** Raggruppa le fonti per documento: "manuale.pdf · pag. 2, 5". */
  private groupSources(references?: RagChatReference[]): MessageSource[] {
    const bySource = new Map<string, Set<number>>();
    (references ?? []).forEach((r) => {
      if (r.page == null) {
        return;
      }
      const pages = bySource.get(r.source) ?? new Set<number>();
      pages.add(r.page);
      bySource.set(r.source, pages);
    });
    return [...bySource.entries()].map(([name, pages]) => ({name, pages: [...pages].sort((a, b) => a - b)}));
  }

  copyAnswer(msg: DocMessage): void {
    navigator.clipboard?.writeText(msg.text).then(() => {
      msg.copied = true;
      setTimeout(() => (msg.copied = false), 2000);
    });
  }

  /** Apre la fonte citata: il PDF corrispondente, alla pagina indicata. */
  openSource(source: MessageSource, page: number): void {
    const name = source.name.toLowerCase();
    const doc = this.documents.find((d) =>
      String(d['original_filename'] ?? '').toLowerCase() === name || `${d.name}.pdf`.toLowerCase() === name,
    );
    if (doc) {
      this.openPdf(doc, page);
    } else {
      this.showError('documents.error_open');
    }
  }

  /**
   * Scarica il PDF decifrato dal backend e lo apre in una nuova scheda.
   * La scheda va aperta subito (nel gesto dell'utente) o il browser la blocca come popup.
   */
  openPdf(doc: ExplorerEntity, page?: number, event?: Event): void {
    event?.stopPropagation();
    if (!doc.uuid) {
      return;
    }
    const win = window.open('', '_blank');
    this.filemanagerApi.getFileBytes(doc.uuid).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(new Blob([blob as unknown as Blob], {type: 'application/pdf'}));
        this.pdfUrls.push(url);
        const target = page ? `${url}#page=${page}` : url;
        if (win) {
          win.location.href = target;
        } else {
          window.open(target, '_blank');
        }
      },
      error: () => win?.close(),
    });
  }

  formatSize(bytes?: number | null): string {
    if (!bytes) {
      return '';
    }
    return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  /** Adatta l'altezza dell'input al contenuto (max ~6 righe, poi scrolla). */
  autoGrowInput(): void {
    const el = this.chatInput?.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }

  private resetInputHeight(): void {
    setTimeout(() => {
      const el = this.chatInput?.nativeElement;
      if (el) {
        el.style.height = 'auto';
      }
    });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  // ---------------------------------------------------------------------------
  // Utente e lingua
  // ---------------------------------------------------------------------------

  setLanguage(code: string): void {
    this.translate.use(code);
    this.coreService.setLanguage(code);
    localStorage.setItem(LANG_KEY, code);
    this.currentLanguage = code;
  }

  logout(): void {
    this.logoutService.logout();
  }

  private showError(key: string): void {
    this.snackBar.open(this.translate.instant(key), this.translate.instant('documents.close'), {duration: 5000});
  }
}
